import { leafBodyHeight } from './waternoseGeometry.js';
import { readGrooveElements, resolveCustomGrooves } from './customGrooves.js';

export const DEFAULT_GROOVES = { count: 0, direction: 'horizontal', side: 'front', widthMm: 10, depthMm: 2 };

// Pattern validation is independent of a particular door size. Keep legacy fields
// unchanged and explicitly retain custom rules when saving or loading the library.
export function readGroovePattern(grooves) {
  const settings = { ...DEFAULT_GROOVES, ...grooves };
  if (settings.mode !== undefined && !['custom', 'regular'].includes(settings.mode)) throw new Error('Unknown groove layout mode.');
  if (!['front', 'back', 'both'].includes(settings.side)) throw new Error('Choose a valid groove face.');
  if (!Number.isFinite(settings.widthMm) || settings.widthMm < 1 || settings.widthMm > 30 ||
      !Number.isFinite(settings.depthMm) || settings.depthMm < 0.2 || settings.depthMm > 10) {
    throw new Error('Grooves must be 1–30 mm wide and 0.2–10 mm deep.');
  }
  const result = Object.fromEntries(Object.keys(DEFAULT_GROOVES).map(key => [key, settings[key]]));
  if (settings.mode === 'custom') {
    const elements = readGrooveElements(settings.elements);
    return { ...result, mode: 'custom', count: elements.reduce((n, e) => n + (e.type === 'rectangle' ? 4 : 1), 0), elements };
  }
  return result;
}

// Equal clear panels, including the first and last panel. Recompute on resize.
export function resolveLeafGrooves({ widthMm, heightMm, grooves, opening = 'outward', waternose, thicknessMm }) {
  heightMm = leafBodyHeight({ heightMm, thicknessMm, waternose });
  const settings = { ...DEFAULT_GROOVES, ...grooves };
  if (settings.mode === 'custom') {
    const pattern = readGroovePattern(settings);
    if (![widthMm, heightMm].every(n => Number.isFinite(n) && n > 0)) throw new Error('Door dimensions must be positive.');
    const layout = resolveCustomGrooves(pattern.elements, widthMm, heightMm, pattern.widthMm);
    if (layout.issues.length) throw new Error(layout.issues.join(' '));
    const localSide = opening === 'inward' && pattern.side !== 'both' ? (pattern.side === 'front' ? 'back' : 'front') : pattern.side;
    return { ...pattern, ...layout, localSide, direction: 'mixed', gapMm: null };
  }
  if (settings.mode !== undefined && settings.mode !== 'regular') throw new Error('Unknown groove layout mode.');
  const { count, direction, side, widthMm: width, depthMm: depth } = settings;
  if (!Number.isInteger(count) || count < 0 || count > 40) throw new Error('Groove count must be an integer from 0 to 40.');
  if (!['horizontal', 'vertical'].includes(direction) || !['front', 'back', 'both'].includes(side)) {
    throw new Error('Choose a valid groove direction and face.');
  }
  if (!Number.isFinite(width) || width < 1 || width > 30 || !Number.isFinite(depth) || depth < 0.2 || depth > 10) {
    throw new Error('Grooves must be 1–30 mm wide and 0.2–10 mm deep.');
  }
  const extent = direction === 'horizontal' ? heightMm : widthMm;
  const gapMm = (extent - count * width) / (count + 1);
  if (count && gapMm < 10) throw new Error('Too many grooves: leave at least 10 mm between grooves and at the edges.');
  const cuts = Array.from({ length: count }, (_, i) => {
    const offset = gapMm + i * (gapMm + width);
    return direction === 'horizontal'
      ? { xMm: 0, yMm: offset, widthMm, heightMm: width }
      : { xMm: offset, yMm: 0, widthMm: width, heightMm };
  });
  const localSide = opening === 'inward' && side !== 'both' ? (side === 'front' ? 'back' : 'front') : side;
  return { ...settings, localSide, gapMm, cuts };
}

// Per-face colour keeps the recess readable even without ambient occlusion.
// The shader multiplies this by the selected paint colour in linear space.
export function grooveVertexColours(position, grooves, frontMm, backMm) {
  const colours = new Float32Array(position.count * 3).fill(1);
  const tolerance = 0.01;
  for (let i = 0; i < position.count; i += 3) {
    const x = (position.getX(i) + position.getX(i + 1) + position.getX(i + 2)) / 3;
    const y = (position.getY(i) + position.getY(i + 1) + position.getY(i + 2)) / 3;
    const z = (position.getZ(i) + position.getZ(i + 1) + position.getZ(i + 2)) / 3;
    const recessed = (grooves.localSide !== 'front' && z < frontMm - tolerance && z >= frontMm - grooves.depthMm - tolerance)
      || (grooves.localSide !== 'back' && z > backMm + tolerance && z <= backMm + grooves.depthMm + tolerance);
    if (recessed && grooves.cuts.some((c) => x >= c.xMm - tolerance && x <= c.xMm + c.widthMm + tolerance
      && y >= c.yMm - tolerance && y <= c.yMm + c.heightMm + tolerance)) {
      colours.fill(0.65, i * 3, (i + 3) * 3);
    }
  }
  return colours;
}
